import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';

// ─── useCategories ────────────────────────────────────────────────────────────
// Aggregates all distinct categories live from the products table via the
// get_categories() SECURITY DEFINER RPC. Returns [{ en, ar, product_count }].
// Safe for both anon (storefront) and authenticated (admin) callers.
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () =>
      supaFetch('POST', 'rpc/get_categories', {}, '', { 'Prefer': 'return=representation' })
        .then(({ data }) => Array.isArray(data) ? data : []),
    staleTime: 30_000,   // 30s — categories change only when products are edited
    gcTime:    5 * 60_000,
  });
}

// ─── useRenameCategory ────────────────────────────────────────────────────────
// Renames (or merges) a category across all products atomically.
// Also sets category_ar for all affected products.
// Requires authenticated staff JWT — enforced by the RPC itself.
export function useRenameCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ oldEn, newEn, newAr }) =>
      supaFetch(
        'POST', 'rpc/rename_category',
        { p_old_en: oldEn, p_new_en: newEn, p_new_ar: newAr || null },
        '', { 'Prefer': 'return=representation' }
      ),
    onSuccess: () => {
      // Invalidate both categories aggregate and the full products list
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// ─── useSetCategoryAr ─────────────────────────────────────────────────────────
// Sets the Arabic translation for an existing category without renaming it.
export function useSetCategoryAr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ en, ar }) =>
      supaFetch(
        'POST', 'rpc/set_category_ar',
        { p_en: en, p_ar: ar },
        '', { 'Prefer': 'return=representation' }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
